import styled from "@emotion/styled";
import CheckCircle from "@assets/check-circle.svg";
import Highlight from "@assets/highlight-unselected.png";
import { HighlightButton } from "./buttons/highlightButton";
import { FlexRowContainer } from "./layout";
import { computePosition, flip, shift, offset } from "@floating-ui/dom";
import { useEffect, useRef, useState } from "react";
import { css } from "@emotion/react";

const StyledAnnotationMenu = styled(FlexRowContainer)`
  box-shadow:
    0px 2px 4px rgba(9, 9, 11, 0.08),
    0px 0px 4px rgba(224, 224, 230, 1),
    0px 0px 2px 1px rgba(255, 255, 255, 1),
    0px 0px 0px 0.25px rgba(234, 234, 238, 1);
  opacity: 1;
  background-color: rgba(244, 244, 246, 1);
  height: 35px;
  border-radius: 8px;
  padding: 10px;
  z-index: 12;
  :hover {
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
  }
`;

export interface Annotation {
  publicationId?: number;
  articleSlug?: string;
  highlightedText: string;
  sectionId: string;
  sectionTitle: string;
  range: Range;
  noteText?: string;
  userId?: number;
}

interface AnnotationMenuProps {
  annotationPosition: DOMRect;
  saveAnnotation: () => void;
  textContainerRef: React.RefObject<HTMLElement | null>;
  annotationSaved: boolean;
  didTapMenuRef?: React.RefObject<boolean>;
}

export const AnnotationMenu = ({
  annotationPosition,
  saveAnnotation,
  textContainerRef,
  annotationSaved,
  didTapMenuRef,
}: AnnotationMenuProps) => {
  const menuRef = useRef<HTMLDivElement>(null);
  const container = textContainerRef.current;
  const textContainerRect = textContainerRef.current?.getBoundingClientRect();
  const [position, setPosition] = useState<{ x?: number; y?: number }>({});
  useEffect(() => {
    if (container && annotationPosition) {
      const refEl = { getBoundingClientRect: () => annotationPosition };
      const floatingEl = document.getElementById("highlight-menu-floating");

      if (!floatingEl || !refEl) {
        setPosition({});
        return;
      }
      computePosition(refEl, floatingEl, {
        placement: "right-end",
        middleware: [
          offset(8),
          flip({ fallbackPlacements: ["bottom-end"] }),
          shift({ padding: 8 }),
        ],
      }).then(({ x, y }) => {
        setPosition({ x, y });
      });
    }
  }, [container, annotationPosition]);

  useEffect(() => {
    const menu = menuRef.current;
    if (!menu || !didTapMenuRef) return;

    const markMenuInteraction = () => {
      didTapMenuRef.current = true;
      setTimeout(() => {
        didTapMenuRef.current = false;
      }, 200);
    };

    menu?.addEventListener("pointerdown", markMenuInteraction);
    return () => {
      menu?.removeEventListener("pointerdown", markMenuInteraction);
    };
  }, [didTapMenuRef]);

  if (!container || !textContainerRect) return null;

  return (
    <StyledAnnotationMenu
      ref={menuRef}
      style={{
        position: "fixed",
        left: position?.x ?? 0,
        top: position?.y ?? 0,
      }}
      id={"highlight-menu-floating"}
    >
      <HighlightButton
        onTouchStart={() => {
          if (!annotationSaved) {
            saveAnnotation();
          }
        }}
        onClick={() => {
          if (!annotationSaved) {
            saveAnnotation();
          }
        }}
        isSaved={annotationSaved}
      >
        <img
          src={annotationSaved ? CheckCircle : Highlight}
          alt="Save"
          title="Save highlight"
          css={css`
            // to override the article image styling
            height: 16px !important;
          `}
        />
      </HighlightButton>
    </StyledAnnotationMenu>
  );
};
